/**
 * Lead Generator - Creates realistic lead data
 * Developed by OWLY Labs
 */

import { faker } from '@faker-js/faker/locale/es';
import { ExtendedLead, Activity, TimelineEvent } from '../core/types';
import { Lead } from '@/types';

// Configure Faker for Colombian context
faker.seed(Date.now());

export class LeadGenerator {
  private static readonly STAGES = [1, 2, 3, 4, 5, 6, 7, 8];
  private static readonly STATUSES: Lead['status'][] = [
    'Caliente', 'Tibio', 'Frío', 'captacion', 'calificacion',
    'cotizacion', 'negociacion', 'separacion'
  ];
  private static readonly CANALES = [
    'Web', 'Referido', 'WhatsApp', 'Llamada', 'Email',
    'Redes Sociales', 'Evento', 'Walk-in'
  ];
  private static readonly PROFESIONES = [
    'Ingeniero', 'Médico', 'Abogado', 'Arquitecto', 'Contador',
    'Administrador', 'Empresario', 'Profesor', 'Consultor'
  ];

  generate(options?: Partial<ExtendedLead>): ExtendedLead {
    const firstName = faker.person.firstName();
    const lastName = faker.person.lastName();
    const fullName = `${firstName} ${lastName}`;

    const stage = options?.stage || faker.helpers.arrayElement(LeadGenerator.STAGES);
    const createdAt = faker.date.recent({ days: 90 }).toISOString();

    const lead: ExtendedLead = {
      id: options?.id || `lead-${faker.string.uuid()}`,
      name: fullName,
      unidadInteres: `${faker.number.int({ min: 101, max: 2505 })}`,
      value: faker.number.int({ min: 200000000, max: 800000000 }),
      stage,
      progress: faker.number.float({ min: 0, max: 1, fractionDigits: 2 }),
      status: faker.helpers.arrayElement(LeadGenerator.STATUSES),
      color: this.getColorByStage(stage),
      size: faker.helpers.arrayElement(['pequeno', 'mediano', 'grande'] as const),
      parqueaderos: faker.number.int({ min: 0, max: 3 }),
      deposito: faker.datatype.boolean(),

      scoring: this.generateScoring(),
      perfil: this.generatePerfil(fullName, createdAt),
      preferencias: this.generatePreferencias(),

      // Extended fields
      activities: [],
      timeline: [],
      interestedUnits: this.generateInterestedUnits(),

      ...options
    };

    // Generate activities after lead is created (use assignedTo if exists)
    const agentId = (options as any)?.assignedTo || (lead as any).assignedTo || `user-${faker.number.int({ min: 1, max: 5 })}`;
    lead.activities = this.generateActivities(lead, agentId);
    lead.timeline = this.generateTimeline(lead);

    return lead;
  }

  private generateScoring() {
    const dimensions = {
      presupuesto: faker.number.int({ min: 60, max: 100 }),
      zona: faker.number.int({ min: 60, max: 100 }),
      datosCompletos: faker.number.int({ min: 70, max: 100 }),
      timing: faker.number.int({ min: 50, max: 100 }),
      credito: faker.number.int({ min: 60, max: 100 })
    };

    const total = Math.round(
      Object.values(dimensions).reduce((a, b) => a + b, 0) / 5
    );

    return { ...dimensions, total };
  }

  private generatePerfil(name: string, createdAt: string) {
    const [firstName, lastName] = name.split(' ');

    return {
      telefono: faker.phone.number('+57 3## ### ####'),
      email: faker.internet.email({ firstName, lastName }).toLowerCase(),
      profesion: faker.helpers.arrayElement(LeadGenerator.PROFESIONES),
      ingresos: faker.number.int({ min: 5000000, max: 30000000 }),
      miembros: faker.number.int({ min: 1, max: 6 }),
      motivacion: faker.helpers.arrayElement([
        'Inversión', 'Vivienda propia', 'Ampliación familiar',
        'Ubicación', 'Rentabilidad'
      ]),
      fechaContacto: createdAt.split('T')[0],
      canal: faker.helpers.arrayElement(LeadGenerator.CANALES)
    };
  }

  private generatePreferencias() {
    return {
      torre: faker.helpers.arrayElement(['Norte', 'Sur', 'Cualquiera'] as const),
      piso: faker.helpers.arrayElement(['Bajo (1-5)', 'Medio (6-15)', 'Alto (16+)', 'Cualquiera']),
      vista: faker.helpers.arrayElement(['Ciudad', 'Montaña', 'Parque', 'Cualquiera']),
      parqueaderos: faker.number.int({ min: 0, max: 3 }),
      deposito: faker.datatype.boolean(),
      financiacion: faker.helpers.arrayElement([
        'Bancolombia', 'Davivienda', 'BBVA', 'Scotiabank',
        'Efectivo', 'Por definir'
      ])
    };
  }

  private generateInterestedUnits() {
    const unitCount = faker.number.int({ min: 1, max: 3 });
    const units: ExtendedLead['interestedUnits'] = [];

    for (let i = 0; i < unitCount; i++) {
      units.push({
        unitId: `unit-${faker.string.uuid()}`,
        projectId: `project-${faker.number.int({ min: 1, max: 3 })}`,
        priority: i + 1,
        status: faker.helpers.arrayElement([
          'interested', 'visited', 'quoted', 'reserved'
        ] as const),
        addedDate: faker.date.recent({ days: 30 }).toISOString().split('T')[0],
        notes: faker.datatype.boolean() ? faker.lorem.sentence() : undefined
      });
    }

    return units;
  }

  private generateActivities(lead: ExtendedLead, agentId: string): Activity[] {
    const count = faker.number.int({ min: 3, max: 12 });
    const activities: Activity[] = [];

    for (let i = 0; i < count; i++) {
      const type = faker.helpers.arrayElement([
        'call', 'email', 'meeting', 'whatsapp', 'note', 'site-visit'
      ] as const);

      const date = faker.date.recent({ days: 60 }).toISOString();

      const baseActivity = {
        id: `activity-${faker.string.uuid()}`,
        type,
        date,
        summary: this.getActivitySummary(type),
        userId: agentId, // Use the assigned agent ID
        entityType: 'lead' as const,
        entityId: lead.id,
        createdAt: date,
        updatedAt: date
      };

      activities.push(this.enhanceActivity(baseActivity, type) as Activity);
    }

    return activities.sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  private enhanceActivity(base: any, type: Activity['type']): any {
    switch (type) {
      case 'call':
        return {
          ...base,
          details: {
            duration: faker.number.int({ min: 60, max: 1800 }),
            outcome: faker.helpers.arrayElement([
              'answered', 'voicemail', 'no-answer', 'busy'
            ] as const),
            sentiment: faker.helpers.arrayElement([
              'positive', 'neutral', 'negative'
            ] as const),
            notes: faker.lorem.paragraph()
          }
        };

      case 'email':
        return {
          ...base,
          details: {
            subject: faker.lorem.sentence(),
            body: faker.lorem.paragraphs(2),
            direction: faker.helpers.arrayElement(['sent', 'received'] as const),
            opened: faker.datatype.boolean(),
            clicked: faker.datatype.boolean()
          }
        };

      case 'meeting':
        return {
          ...base,
          details: {
            location: faker.helpers.arrayElement([
              'Oficina', 'Sala de proyectos', 'Videollamada', 'Cafe'
            ]),
            duration: faker.number.int({ min: 15, max: 120 }),
            attendees: [`user-${faker.number.int({ min: 1, max: 5 })}`],
            outcome: 'completed' as const,
            notes: faker.lorem.paragraph(),
            nextSteps: [faker.lorem.sentence(), faker.lorem.sentence()]
          }
        };

      case 'whatsapp':
        return {
          ...base,
          details: {
            messageCount: faker.number.int({ min: 1, max: 15 }),
            lastMessage: faker.lorem.sentence(),
            conversationUrl: `/chat/${base.entityId}`,
            hasMedia: faker.datatype.boolean()
          }
        };

      case 'note':
        return {
          ...base,
          details: {
            content: faker.lorem.paragraph(),
            category: faker.helpers.arrayElement([
              'general', 'follow-up', 'important', 'reminder'
            ] as const),
            tags: faker.helpers.arrayElements(['urgente', 'seguimiento', 'info'], { min: 0, max: 2 })
          }
        };

      case 'site-visit':
        return {
          ...base,
          details: {
            projectId: `project-${faker.number.int({ min: 1, max: 3 })}`,
            unitIds: [`unit-${faker.string.uuid()}`],
            duration: faker.number.int({ min: 30, max: 180 }),
            feedback: faker.lorem.paragraph(),
            interest_level: faker.helpers.arrayElement(['high', 'medium', 'low'] as const),
            nextSteps: faker.datatype.boolean()
              ? [faker.lorem.sentence()]
              : undefined
          }
        };
    }
  }

  private getActivitySummary(type: Activity['type']): string {
    const summaries = {
      call: ['Llamada de seguimiento', 'Contacto inicial', 'Actualización de información'],
      email: ['Envío de cotización', 'Información del proyecto', 'Recordatorio de cita'],
      meeting: ['Reunión de presentación', 'Visita al proyecto', 'Negociación de condiciones'],
      whatsapp: ['Conversación WhatsApp', 'Intercambio de mensajes', 'Consulta rápida'],
      note: ['Nota interna', 'Recordatorio', 'Actualización de estado'],
      'site-visit': ['Visita al proyecto', 'Recorrido por unidades', 'Inspección de muestra']
    };

    return faker.helpers.arrayElement(summaries[type]);
  }

  private generateTimeline(lead: ExtendedLead): TimelineEvent[] {
    const events: TimelineEvent[] = [];

    // Initial contact
    events.push({
      id: `timeline-${faker.string.uuid()}`,
      date: lead.perfil.fechaContacto,
      type: 'milestone',
      title: 'Primer contacto',
      description: `Lead ingresó vía ${lead.perfil.canal}`,
      color: 'blue'
    });

    // Stage milestones
    if (lead.stage >= 2) {
      events.push({
        id: `timeline-${faker.string.uuid()}`,
        date: faker.date.between({
          from: lead.perfil.fechaContacto,
          to: new Date()
        }).toISOString().split('T')[0],
        type: 'milestone',
        title: 'Calificación BANT completada',
        description: 'Lead calificado y validado',
        color: 'green'
      });
    }

    if (lead.stage >= 4) {
      events.push({
        id: `timeline-${faker.string.uuid()}`,
        date: faker.date.recent({ days: 15 }).toISOString().split('T')[0],
        type: 'milestone',
        title: 'Cotización enviada',
        description: 'Propuesta económica presentada',
        color: 'purple'
      });
    }

    // Activity highlights
    const importantActivities = lead.activities
      .filter(a => a.type === 'meeting' || a.type === 'site-visit')
      .slice(0, 3);

    importantActivities.forEach(activity => {
      events.push({
        id: `timeline-${faker.string.uuid()}`,
        date: activity.date.split('T')[0],
        type: 'activity',
        title: activity.summary,
        description: activity.type === 'meeting' ? 'Reunión completada' : 'Visita al proyecto',
        color: 'amber'
      });
    });

    return events.sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }

  private getColorByStage(stage: number): string {
    const colors: Record<number, string> = {
      1: '#3b82f6', // blue
      2: '#8b5cf6', // purple
      3: '#ec4899', // pink
      4: '#f59e0b', // amber
      5: '#10b981', // green
      6: '#06b6d4', // cyan
      7: '#22c55e', // success
      8: '#ef4444'  // red
    };
    return colors[stage] || '#6b7280';
  }

  generateBatch(count: number, options?: Partial<ExtendedLead>): ExtendedLead[] {
    return Array.from({ length: count }, () => this.generate(options));
  }
}

export const leadGenerator = new LeadGenerator();
